import { memberApi, memberApiV2 } from '../lib/api';
import { VITAL_DATA_API_TYPES_UTIL } from '../lib/api/vital-data-api';
import {
  ITimelineData,
  IVitalDailyCount,
  IVitalSearchParams,
  IVitalSearchState,
  TTimelineStep,
  TVitalDataPost,
} from '../types/vital-data';
import { IDateRange, TVitalDataSearchItems, TVitalSettingsResponse } from '../types/life-id';

/**
 * Get vital data summary for life id
 */
export function getVitalDataSummary() {
  return memberApi.request('/life-id/vital-data/summary');
}

/**
 * Get paginated result with vital data
 * @param type type of data
 * @param options
 */
export function searchVitalDataByType(
  type: keyof typeof VITAL_DATA_API_TYPES_UTIL,
  options: IVitalSearchParams,
) {
  return memberApi.request<{ data: IVitalSearchState }>(
    `/life-id/vital-data/${VITAL_DATA_API_TYPES_UTIL[type].urlPart}/search`,
    {
      method: 'POST',
      data: options,
    },
  );
}

/**
 * Add single record of vital data
 * @param type type of record
 * @param data data to add
 */
export function addVitalRecord(
  type: keyof typeof VITAL_DATA_API_TYPES_UTIL,
  data: TVitalDataPost & { userId: string },
) {
  return memberApi.request(
    `/life-id/vital-data/${VITAL_DATA_API_TYPES_UTIL[type].urlPart}/single`,
    {
      method: 'POST',
      data,
    },
  );
}

/**
 * Get timeline for graphs detail in vital data
 * @param type
 * @param step
 * @param dateFrom
 * @param dateTo
 * @param userId
 * @param subType
 */
export function getVitalTimeline(
  type: keyof typeof VITAL_DATA_API_TYPES_UTIL,
  step: TTimelineStep,
  dateFrom: string,
  dateTo: string,
  userId: string,
  subType?: string,
) {
  return memberApi.request<{ data: ITimelineData }>(
    `/life-id/vital-data/${VITAL_DATA_API_TYPES_UTIL[type].urlPart}/timeline`,
    {
      method: 'GET',
      params: {
        step,
        dateTo,
        dateFrom,
        type: subType,
        userId,
      },
    },
  );
}

export function getVitalTimelineV2(
  type: keyof typeof VITAL_DATA_API_TYPES_UTIL,
  step: TTimelineStep,
  dateFrom: string,
  dateTo: string,
  userId: string,
  providerId: string,
  subType?: string,
) {
  return memberApiV2.request<{ data: ITimelineData }>(
    `/management/private/life-id/vital-data/${VITAL_DATA_API_TYPES_UTIL[type].urlPart}/timeline`,
    {
      method: 'GET',
      headers: {
        'provider-id': providerId,
      },
      params: {
        userId,
        step,
        dateTo,
        dateFrom,
        type: subType,
      },
    },
  );
}

/**
 * Remove record from DB
 * @param type type fo data
 * @param id id of record
 */
export function removeVitalData(type: keyof typeof VITAL_DATA_API_TYPES_UTIL, id: string) {
  return memberApi.request(
    `/life-id/vital-data/${VITAL_DATA_API_TYPES_UTIL[type].urlPart}/single`,
    {
      method: 'DELETE',
      data: {
        id,
      },
    },
  );
}

/**
 *  Get count of records grouped by date
 * @param type type of vital data
 * @param page page to get
 * @param pageSize size to get
 */
export function getVitalDailyCount(
  type: keyof typeof VITAL_DATA_API_TYPES_UTIL,
  page: number,
  pageSize: number,
) {
  return memberApi.request<{ data: IVitalDailyCount }>(
    `/life-id/vital-data/daily-count/${VITAL_DATA_API_TYPES_UTIL[type].urlPart}`,
    {
      method: 'GET',
      params: {
        page,
        pageSize,
      },
    },
  );
}

/**
 * Get ranges for vital data visualizations and entry ranges
 */

export const getVitalDataRanges = (providerId: string, id: string) =>
  memberApiV2.request(`/management/private/life-id/vital-data/intervals`, {
    headers: {
      'provider-id': providerId,
    },
    params: {
      userId: id,
    },
  });

export function getHealthRateEntries(
  userId: string,
  urlPart: string,
  page = 1,
  pageSize = 20,
  range?: IDateRange,
) {
  return memberApi.request<{ data: TVitalDataSearchItems }>(
    `/life-id/vital-data/${urlPart}/search/${userId}`,
    {
      method: 'POST',
      data: {
        userId,
        page,
        pageSize,
        source: null,
        from: range ? range.from : null,
        to: range ? range.to : null,
      },
    },
  );
}

export function getHealthRateEntriesV2(
  userId: string,
  urlPart: string,
  page = 1,
  pageSize = 20,
  providerId: string,
  range?: IDateRange,
) {
  return memberApiV2.request<{ data: TVitalDataSearchItems }>(
    `/management/private/life-id/vital-data/${urlPart}/search`,
    {
      method: 'POST',
      params: {
        userId,
      },
      headers: {
        'provider-id': providerId,
      },
      data: {
        userId,
        page,
        pageSize,
        source: null,
        from: range ? range.from : null,
        to: range ? range.to : null,
      },
    },
  );
}

export const getVitalDataById = (providerId: string, id: string) =>
  memberApiV2.request(`/management/private/life-id/vital-data/summary`, {
    headers: {
      'provider-id': providerId,
    },
    params: {
      userId: id,
    },
  });
